import { Timestamp } from '@angular/fire/firestore';
import { Doc } from 'src/app/document/document.model';
import { ITile } from 'src/app/shared/regular-grid/regular-grid.component';

export enum MovementSource {
  none = 0,
  face,
  hands,
  lefHand,
  rightHand,
  eyeTracker,
  tobii,
  touchscreen,
  navigationCursor,
  mouse
}

export enum Driver {
  none = 0,
  face,
  hands,
  eyeTracker,
  peyeTracker,
  touchscreen,
  rastreador,
  mouse,
  gazeWeb
}

export enum ActionFilterClass {
  none = 0,
  mouse,
  keyboard,
  automation,
  settings, // Changes settings withing this software
  scan,
  control,
  sound,
  cursorPosition,
  calibration = 100, // Apenas valores menores do que 100 são exibidos na configuração manual!
}

export enum MovementFilterClass {
  none = 0,
  head,
  face,
  hand,
  finger,
}

export enum DiscreteEventType {
  none = 0,
  pauseScan, // Pausa ou congela cursor
  suspendScan, // Tira o cursor da tela
  resumeScan,
  select,
  cancelSelection,
  suggestion1,
  suggestion2,
  suggestion3,
  vocalize,
  erase,
  space,
  wizardEvent, // For monitoring generic events during wizard tests
  wizardEventEnd,
  undo, // Forma mais genérica do cancelar / apagar / voltar
}

export enum DigitalEventType {
  none = 0,
  cursorPositionH,
  cursorPositionV,
  cursorPositionHV,
  feedbackPositionH,
  feedbackPositionV,
  feedbackPositionHV,
}

export enum MappingsOutputType {
  none = 0,
  cursorPositionH,
  cursorPositionV,
  cursorPositionHV,
  feedbackPositionH,
  feedbackPositionV,
  feedbackPositionHV,
  pauseScan, // Pausa ou congela cursor
  suspendScan, // Tira o cursor da tela
  resumeScan,
  select,
  feedbackSelect,
  cancelSelection,
  suggestion1,
  suggestion2,
  suggestion3,
  vocalize,
  erase, 
  space,
  wizardEvent, // For monitoring generic events during wizard tests
  wizardEventEnd,
  undo, // Forma mais genérica do cancelar / apagar / voltar
  repeat,
  cancelSelectionAuto,
}

// export enum FunctionIds {
//   none = 0,
//   findJawOpen,

// }

export interface ActionOption {
  name?: string; // Display Name
  function: string; // The function that performs the output action
  filterClass?: ActionFilterClass;
}

export interface MovementOption {
  name: string; // Display Name
  function: string; // The function that performs the output action
  source: MovementSource; // Source of the data points used by the movement detection function
  driver?: Driver | Array<Driver>; // Driver used to capture the data points 
  filterClass?: MovementFilterClass;
}

export interface SoundOption {
  name: string; // Display Name
  function: string; // The function that performs the output action
  filterClass?: ActionFilterClass;
}

export interface Mappings {
  addrName: string; // Name of the address profile
  name: string; // Name of the receiver
  address1: string; // Address Line 1
  address2: string; // Address Line 2
  address3: string; // Address Line 3
  city: string; // City name
  province: string; // Province or State
  country: string; // Country Name
  zipCode: string; // Postal Code
  phone: string; // Phone number
  reference: string; // Address reference for deliveries

  timestamp: Timestamp; // Creation timestamp
}

export interface MappingsServiceSettings {
  enabled: boolean;
  priority: number;
  mappings: Array<MappingsItemSettings>; // Array of mappings
}

export interface MappingsServiceState {
  muted: boolean;
  mappings: Array<MappingsItemState>; // Array containing mapping states
  dispatchMouse: boolean;
}

// não devemos usar tipos híbridos pois gera problemas na camada nativa
export interface MappingsItemSettings {
  name?: string; // Display Name
  enabled: boolean; // If this gesture is enabled
  priority?: number; // Relative priority if multiple gestures are detected
  movement: MovementOption; // Function that receives data from source and returns the detection score
  detectionThreshold?: number; // The score beyond which the action is performed
  holdTime?: number; // The minimum hold time in ms required to trigger
  hysteresis?: number; // The hysteresis amount
  detectionAction?: ActionOption; // The function that performs the output action
  detectionEndAction?: ActionOption; // The function that performs the output action
  actionArg?: any; // Extra args for the action function
  actionSound?: ActionOption; // Sound played with the action
  actionSoundVolume?: number; // Volume of the sound played with the action
  preDetectionAction?: ActionOption; // The function called when trigger is above threshold, but other requirements are not yet met
  preDetectionCancelAction?: ActionOption; // The function called when trigger returns below threshold, but other requirements were not met
  rescaleMax?: number; // Max of the user calibrated range  // deprecated
  rescaleMin?: number; // Min of the user calibrated range  // deprecated
  range?: {lower: number, upper: number}; // User calibrated range
  rescaleTranslateWindow?: boolean; // If the rescale window should be translated when the limits are reached
  resolution?: number; // Max number of steps used for discretization
  visible?: boolean; // If this mapping is visible in the UI
  longAction?: ActionOption; // The function that performs the output action for long hold
  longActionTime?: number; // The minimum hold time in ms required to trigger long hold
  required?: boolean; // If this mapping is required / can't be removed
  visibilityMask?: Array<string>; // a mask of the visibility of each key in this mapping
}

export interface MappingsItemState {
  detectionLevel: number;
  rescaledValue: number;
  discretizedValue: number;
  lastHysteresisRef: number; // Last output sent as digital value - used for hysteresis
  lastDelta: number; // Last increment or decrement sent as digital value - used for hysteresis
  output: number;
  detected: boolean;
  suspected: boolean; // If simple threshold criteria is met
  startTimestamp: number;
  dispatchPending: boolean;
}

export interface BinaryEvent {
  event: DiscreteEventType;
  data?: any;
  detected?: boolean;
  captureTime?: number;
}

export interface DigitalEvent {
  event: DigitalEventType;
  value?: number; // Current value
  resolution?: number; // Number of possible values 6 if possible values are [0,1,2,3,4,5]
  confidence?: number; // Percent of confidence in 0 to 1 range
  captureTime?: number;
}

export interface MappingsOutput {
  type: MappingsOutputType;
  value?: number; // Current value
  resolution?: number; // Number of possible values 6 if possible values are [0,1,2,3,4,5]
  captureTime?: number;
}

export interface ActionLog {
  action: string;
  card?: ITile;
  document?: string;
  block?: string;
  item?: string;
  index?: number;
  data?: any;
  style?: string;
  // TODO: Precisa corrigir o tipo do timestamp
  timestamp?: string;
  // timestamp?: Timestamp;
}